import React, { useState, useEffect } from 'react'
import { X, Shield } from 'lucide-react'
import './styles.scss'

export default function AssignRoleModal({ isOpen, onClose, onSave, programs, initialEmail = '', foundUser = null }) {
  const [email, setEmail] = useState('')
  const [role, setRole] = useState('STAFF')
  const [programId, setProgramId] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isOpen) {
      setEmail(initialEmail)
      
      if (foundUser && foundUser.roles) {
        if (foundUser.roles.includes('SUPER_ADMIN') || foundUser.roles.includes('ADMIN')) {
          setRole('ADMIN')
        } else if (foundUser.roles.includes('AGENT')) {
          setRole('AGENT')
        } else {
          setRole('STAFF')
          if (foundUser.managedProgramIds && foundUser.managedProgramIds.length > 0) {
            setProgramId(foundUser.managedProgramIds[0])
          }
        }
      } else {
        setRole('STAFF')
        setProgramId('')
      }
    }
  }, [isOpen, initialEmail, foundUser])

  if (!isOpen) return null

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    
    if (!email.trim()) {
      setError('Email is required')
      return
    }

    if (role === 'STAFF' && !programId) {
      setError('Please assign a program to the STAFF member')
      return
    }

    setLoading(true)
    try {
      await onSave({ email: email.trim(), role, programId })
      // Reset form
      setEmail('')
      setRole('STAFF')
      setProgramId('')
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign role. The user may not exist.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content fade-in">
        <div className="modal-header">
          <div className="header-left">
            <div className="modal-icon">
              <Shield size={20} />
            </div>
            <div>
              <h2 className="modal-title">Assign Role</h2>
              <p className="modal-subtitle">Add a user to your university team</p>
            </div>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div className="form-error">
              {error}
            </div>
          )}

          <div className="form-group">
            <label>User Email Address *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. agent@example.com"
              required
              disabled={loading}
              readOnly
              className="input-readonly"
            />
          </div>

          <div className="form-group">
            <label>Role *</label>
            <select
              value={role}
              onChange={(e) => {
                setRole(e.target.value)
                if (e.target.value !== 'STAFF') setProgramId('')
              }}
              disabled={loading}
            >
              <option value="STAFF">STAFF (Requires Program Assignment)</option>
              <option value="ADMIN">ADMIN (University Wide Access)</option>
              <option value="AGENT">AGENT (Can be assigned to a specific Program)</option>
            </select>
          </div>

          {(role === 'STAFF' || role === 'AGENT') && (
            <div className="form-group">
              <label>Assign to Program {role === 'STAFF' ? '*' : '(Optional)'}</label>
              <select
                value={programId}
                onChange={(e) => setProgramId(e.target.value)}
                required={role === 'STAFF'}
                disabled={loading}
              >
                <option value="" disabled>Select Program...</option>
                {programs.filter(p => p.active).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="modal-actions">
            <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Saving...' : 'Assign Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
